import { lazy, Suspense } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import BaseLayout from './components/BaseLayout'
import PageLoading from './components/PageLoading'
import { useProfilesSelector } from './lib/profiles'
import WelcomeComponent from './components/WelcomeComponent'
import useAcceptEmployerInvitation from './lib/useAcceptEmployerInvitation'
import useAcceptInvitation from './lib/useAcceptInvitation'
import { AdminOnly } from './AdminOnly'
import { MyAccount } from './pages/my-account'
import { HelpCenterPage } from './components/HelpCenter/HelpCenterPage'
import useAcceptTaxAdvisorInvitation from './lib/useAcceptTaxAdvisorInvitation'
import useVersionCheck from './lib/useVersionCheck'
import ErrorBoundary from './ErrorBoundary'
import { AgentRunNotificationListener } from './components/AgentRunNotificationListener'

const Employee = lazy(() => import('./pages/employee'))
const Employer = lazy(() => import('./pages/employer'))
const Admin = lazy(() => import('./pages/admin'))
const Operations = lazy(() => import('./pages/operations'))
const TaxAdvisor = lazy(() => import('./pages/tax-advisor'))

export default function App() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  useVersionCheck()

  useAcceptEmployerInvitation(searchParams, setSearchParams, (companyUid) => {
    // On success forward to the dashboard of the invited profile
    navigate(`/employer/${companyUid}/dashboard`)
  })

  useAcceptInvitation(searchParams, setSearchParams, (contractUid) => {
    // On success forward to the dashboard of the invited profile
    navigate(`/employee/${contractUid}/dashboard`)
  })

  useAcceptTaxAdvisorInvitation(
    searchParams,
    setSearchParams,
    (taxAdvisorUid) => {
      // On success forward to the dashboard of the invited profile
      navigate(`/tax-advisor/${taxAdvisorUid}/dashboard`)
    }
  )

  return (
    <BaseLayout>
      <ErrorBoundary>
        <AgentRunNotificationListener />
      </ErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <AppRoutes />
      </Suspense>
    </BaseLayout>
  )
}

function AppRoutes() {
  const profiles = useProfilesSelector()
  const location = useLocation()

  // Prefer first active profile (inactive is falsy or undefined) over inactive ones
  const defaultProfile = profiles.find((p) => !p.inactive) ?? profiles[0]

  return (
    <Routes>
      <Route
        index
        element={
          defaultProfile ? (
            <Navigate to={`${defaultProfile.link}${location.search}`} />
          ) : (
            <WelcomeComponent />
          )
        }
      />
      <Route path="/employee/:contractUid/*" element={<Employee />} />
      <Route path="/employer/:companyUid/*" element={<Employer />} />
      <Route path="/operations/*" element={<Operations />} />
      <Route path={'/tax-advisor/:taxAdvisorUid/*'} element={<TaxAdvisor />} />
      <Route path="/my-account/*" element={<MyAccount />} />
      <Route path="/help/*" element={<HelpCenterPage />} />
      <Route
        path="/admin/*"
        element={
          <AdminOnly>
            <Admin />
          </AdminOnly>
        }
      />
      <Route render={() => <Navigate to={`/${location.search}`} />} />
    </Routes>
  )
}
